import { Static } from "@sinclair/typebox";
import { Syntax } from '@sinclair/typebox/syntax';
import { jwtDecode } from "jwt-decode";

/**
 * The provider data for the OpenID authentication provider.
 * The shape of this object was gleaned from the Zudoku codebase.
 */
export const OpenIdProviderDataSchema = Syntax(`{
    accessToken: string;
    idToken?: string;
    refreshToken?: string;
    expiresOn: Date | string;
    tokenType: string;
}`);

export type OpenIdProviderData = Static<typeof OpenIdProviderDataSchema>;

export function hasAppStoreEntitlement(accessToken: string): boolean {
  if (!accessToken) return false;
  try {
    const decoded = jwtDecode(accessToken);

    for (const value of Object.values(decoded)) {
      if (Array.isArray(value)) {
        const hasClaim = value.some(
          (v) =>
            typeof v === "string" &&
            // Platform changed the claim name from APPSTORE_ to DP_.
            // Support both for now.
            v.toUpperCase().search(/^(APPSTORE_|DP_)/) !== -1
        );
        if (hasClaim) return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}

const PLATFORM_ENTITY_CLAIMS_PREFIX =
  "https://opengov.com/platform/claims/v1/entity/*";
const PLC_SUPERUSER_CLAIM = "PLC_SUPERUSER";
const PLATFORM_USER_KEY = "https://opengov.com/platform_user";
const OPENGOV_ADMIN_ROLE = "opengov_admin";

export function isPlcSuperuser(accessToken: string): boolean {
  if (!accessToken) return false;
  try {
    const decoded = jwtDecode<Record<string, unknown>>(accessToken);

    const hasSuperuserClaim = Object.entries(decoded).some(
      ([key, value]) =>
        key.startsWith(PLATFORM_ENTITY_CLAIMS_PREFIX) &&
        Array.isArray(value) &&
        value.includes(PLC_SUPERUSER_CLAIM)
    );
    if (hasSuperuserClaim) return true;

    const platformUser = decoded[PLATFORM_USER_KEY];
    return (
      typeof platformUser === "object" &&
      platformUser !== null &&
      (platformUser as { delphius_role?: unknown }).delphius_role ===
        OPENGOV_ADMIN_ROLE
    );
  } catch {
    return false;
  }
}

export function isOpenGovEmail(providerData: any): boolean {
  try {
    if (!providerData?.accessToken) return false;
    const decoded: any = jwtDecode(providerData.accessToken);
    const email = decoded?.email;
    return typeof email === "string" && /@opengov\.com$/i.test(email);
  } catch {
    return false;
  }
}
